import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, ExternalLink, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { projectId, publicAnonKey } from '../../../utils/supabase/info';

interface Advert {
  id: string;
  title: string;
  description: string;
  imageUrl: string;
  linkUrl?: string;
  duration: 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'semi-annual' | 'yearly';
  startDate: string;
  endDate: string;
  isActive: boolean;
  advertiser: string;
  views?: number;
  clicks?: number;
}

interface AdvertBannerProps {
  position?: 'top' | 'bottom' | 'middle';
  className?: string;
}

export function AdvertBanner({ position = 'middle', className = '' }: AdvertBannerProps) {
  const [currentAd, setCurrentAd] = React.useState<Advert | null>(null);
  const [isVisible, setIsVisible] = React.useState(true);
  const [isLoading, setIsLoading] = React.useState(true);

  // Load active adverts
  React.useEffect(() => {
    loadActiveAd();
  }, []);

  const loadActiveAd = async () => {
    try {
      setIsLoading(true);
      
      // Check if user has dismissed ads recently (respect user choice)
      const dismissedUntil = localStorage.getItem('sentlo-ad-dismissed-until');
      if (dismissedUntil && new Date(dismissedUntil) > new Date()) {
        setIsVisible(false);
        setIsLoading(false);
        return;
      }

      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-684c7e75/adverts/active`,
        {
          headers: {
            'Authorization': `Bearer ${publicAnonKey}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.advert) {
          setCurrentAd(data.advert);
          // Track view
          trackAdView(data.advert.id);
        }
      }
    } catch (error) {
      console.error('Failed to load advert:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const trackAdView = async (advertId: string) => {
    try {
      await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-684c7e75/adverts/${advertId}/view`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${publicAnonKey}`
          }
        }
      );
    } catch (error) {
      console.error('Failed to track ad view:', error);
    }
  };

  const trackAdClick = async (advertId: string) => {
    try {
      await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-684c7e75/adverts/${advertId}/click`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${publicAnonKey}`
          }
        }
      );
    } catch (error) {
      console.error('Failed to track ad click:', error);
    }
  };

  const handleAdClick = () => {
    if (!currentAd) return;

    trackAdClick(currentAd.id);

    if (currentAd.linkUrl) {
      window.open(currentAd.linkUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleDismiss = () => {
    setIsVisible(false);
    // Remember dismissal for 1 hour
    const dismissUntil = new Date();
    dismissUntil.setHours(dismissUntil.getHours() + 1);
    localStorage.setItem('sentlo-ad-dismissed-until', dismissUntil.toISOString());
    
    toast.info('Ad hidden', {
      description: 'Ads support Sentlo. They will return in 1 hour.'
    });
  };

  if (isLoading || !currentAd || !isVisible) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: position === 'top' ? -20 : 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: position === 'top' ? -20 : 20 }}
        className={`relative ${className}`}
      >
        {/* Sponsored Label */}
        <div className="flex items-center justify-between mb-2 px-2">
          <div className="flex items-center gap-1.5">
            <AlertCircle className="w-3 h-3 text-gray-400" />
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
              Sponsored
            </span>
          </div>
          <button
            onClick={handleDismiss}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
            title="Hide ad for 1 hour"
          >
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        {/* Ad Banner */}
        <motion.div
          onClick={handleAdClick}
          className={`relative overflow-hidden rounded-2xl border-2 border-gray-200 bg-white shadow-lg ${
            currentAd.linkUrl ? 'cursor-pointer hover:shadow-xl' : ''
          } transition-all duration-300`}
          whileHover={currentAd.linkUrl ? { scale: 1.02 } : {}}
          whileTap={currentAd.linkUrl ? { scale: 0.98 } : {}}
        >
          {/* Ad Image */}
          {currentAd.imageUrl && (
            <div className="relative w-full aspect-[16/9] sm:aspect-[21/9] overflow-hidden bg-gradient-to-br from-blue-50 to-purple-50">
              <img
                src={currentAd.imageUrl}
                alt={currentAd.title}
                className="w-full h-full object-cover"
                loading="lazy"
              />
              
              {/* Gradient Overlay for text readability */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              
              {/* Ad Content Overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6">
                <h3 className="text-white font-black text-lg sm:text-xl mb-1 drop-shadow-lg">
                  {currentAd.title}
                </h3>
                {currentAd.description && (
                  <p className="text-white/90 text-sm font-semibold drop-shadow-lg line-clamp-2">
                    {currentAd.description}
                  </p>
                )}
                {currentAd.linkUrl && (
                  <div className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-white/90 backdrop-blur-sm rounded-full text-sm font-black text-gray-900">
                    Learn More
                    <ExternalLink className="w-4 h-4" />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Fallback for no image */}
          {!currentAd.imageUrl && (
            <div className="p-6 bg-gradient-to-br from-blue-500 to-purple-600 text-white">
              <h3 className="font-black text-xl mb-2">{currentAd.title}</h3>
              {currentAd.description && (
                <p className="text-white/90 text-sm font-semibold mb-4">
                  {currentAd.description}
                </p>
              )}
              {currentAd.linkUrl && (
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-sm font-black">
                  Learn More
                  <ExternalLink className="w-4 h-4" />
                </div>
              )}
            </div>
          )}
        </motion.div>

        {/* Advertiser Info */}
        {currentAd.advertiser && (
          <div className="mt-2 px-2">
            <span className="text-[10px] text-gray-400 font-semibold">
              By {currentAd.advertiser}
            </span>
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
}